import { FC, useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { trainingConstructorStore } from '@pages/TrainingConstructor/stores'
import Modal from 'react-modal'
import { AnimatePresence, motion } from 'framer-motion'
import { ExerciseParams } from '@pages/TrainingConstructor/types'
import { ExercisesAccordion } from './exercisesAccordion'
import { useCreateTraining } from '@entities/training/hooks/crud.create'
import { CreateTrainingResponse } from '@entities/training/types/create'
import { Bike, Lock, Plus, X } from 'lucide-react'
import { ErrorModal } from '@shared/layout/Modal/errorModal'
import './trainingFormModal.css'

interface TrainingFormModalProps {
	isOpen: boolean
	setIsOpen: (isOpen: boolean) => void
	exercises: ExerciseParams[]
	prevTitle?: string
	prevDescription?: string
	draftTitle?: string
	draftDescription?: string
	onDraftChange?: (draft: { title: string; description: string }) => void
	onUpdate?: (exercises: ExerciseParams[], title: string, description: string) => void
	onDelete?: () => void
	onCreateSuccess?: (training: CreateTrainingResponse) => void
	title?: string
	submitLabel?: string
	hideSecondaryAction?: boolean
}

const TITLE_LIMIT = 60
const DESCRIPTION_LIMIT = 200

function formatExerciseCount(count: number): string {
	return `Упражнения (${count})`
}

export const FormTrainingModal: FC<TrainingFormModalProps> = observer(
	({
		isOpen,
		setIsOpen,
		onDelete,
		prevTitle,
		prevDescription,
		draftTitle,
		draftDescription,
		onDraftChange,
		onUpdate,
		exercises,
		onCreateSuccess,
		title: titleProp,
		submitLabel,
		hideSecondaryAction = false,
	}) => {
		const closeModal = () => setIsOpen(false)
		const { clearExercises } = trainingConstructorStore
		const isEditMode = Boolean(onUpdate || onDelete)
		const headerTitle =
			titleProp ?? (isEditMode ? 'Изменение тренировки' : 'Создание тренировки')
		const mainButtonTitle =
			submitLabel ?? (isEditMode ? 'Сохранить изменения' : 'Создать тренировку')
		const [localTitle, setLocalTitle] = useState(prevTitle ?? '')
		const [localDescription, setLocalDescription] = useState(
			prevDescription ?? '',
		)
		const title = onDraftChange ? draftTitle ?? '' : localTitle
		const description = onDraftChange
			? draftDescription ?? ''
			: localDescription
		const [isDescriptionOpen, setIsDescriptionOpen] = useState(
			Boolean(draftDescription ?? prevDescription),
		)
		const [exerciseParams, setExerciseParams] =
			useState<ExerciseParams[]>(exercises)

		const { mutateAsync: createTraining, isLoading } = useCreateTraining()
		const [showErrorModal, setShowErrorModal] = useState(false)

		useEffect(() => {
			if (!isOpen || onDraftChange) {
				return
			}

			setLocalTitle(prevTitle ?? '')
			setLocalDescription(prevDescription ?? '')
			setIsDescriptionOpen(Boolean(prevDescription))
			setExerciseParams(exercises)
		}, [exercises, isOpen, onDraftChange, prevDescription, prevTitle])

		const updateTitle = (nextTitle: string) => {
			setLocalTitle(nextTitle)
			onDraftChange?.({ title: nextTitle, description })
		}

		const updateDescription = (nextDescription: string) => {
			setLocalDescription(nextDescription)
			onDraftChange?.({ title, description: nextDescription })
		}

		const canSubmit = useMemo(
			() => exerciseParams.length > 0 && title.trim().length > 0 && !isLoading,
			[exerciseParams.length, isLoading, title],
		)

		const deleteExercise = (exerciseId: number) => {
			setExerciseParams((prev) =>
				prev
					.filter((exercise) => exercise.exercise_id !== exerciseId)
					.map((exercise, index) => ({ ...exercise, order: index + 1 })),
			)
		}

		const clearModal = () => {
			setLocalTitle('')
			setLocalDescription('')
			setExerciseParams([])
			onDraftChange?.({ title: '', description: '' })
			closeModal()
		}

		const clearTrainingCreation = () => {
			clearExercises()
			clearModal()
		}

		const handleCreateSuccess = (createdTraining: CreateTrainingResponse) => {
			onCreateSuccess?.(createdTraining)
			clearTrainingCreation()
		}

		const handleMainButtonClick = async () => {
			if (!canSubmit) {
				return
			}

			if (onUpdate) {
				onUpdate(exerciseParams, title.trim(), description.trim())
				closeModal()
				return
			}

			try {
				const createdTraining = await createTraining({
					title: title.trim(),
					description: description.trim(),
					exerciseParams,
				})
				handleCreateSuccess(createdTraining)
			} catch {
				setShowErrorModal(true)
			}
		}

		const handleClearClick = () => {
			if (onDelete) {
				onDelete()
				clearModal()
				return
			}

			clearTrainingCreation()
		}

		return (
			<>
			<Modal
				isOpen={isOpen}
				onRequestClose={closeModal}
				shouldCloseOnOverlayClick
				className="training-form-modal"
				overlayClassName="training-form-modal-overlay"
			>
				<motion.div
					className="training-form-sheet"
					initial={{ y: '100%', opacity: 0.96 }}
					animate={{ y: 0, opacity: 1 }}
					exit={{ y: '100%', opacity: 0.96 }}
					transition={{ duration: 0.42, ease: [0.16, 1, 0.3, 1] }}
				>
					<div className="training-form-handle" aria-hidden="true" />
					<header className="training-form-header">
						<h2>{headerTitle}</h2>
						<button
							type="button"
							className="training-form-close"
							onClick={closeModal}
							aria-label={
								isEditMode
									? 'Закрыть изменение тренировки'
									: 'Закрыть создание тренировки'
							}
						>
							<X aria-hidden="true" />
						</button>
					</header>

					<div className="training-form-content">
						<label className="training-form-title-field">
							<Bike aria-hidden="true" />
							<input
								value={title}
								maxLength={TITLE_LIMIT}
								onChange={(event) => updateTitle(event.target.value)}
								placeholder="Название тренировки"
								aria-label="Название тренировки"
							/>
							<span>{title.length}/{TITLE_LIMIT}</span>
						</label>

						<div className="training-form-description">
							{!isDescriptionOpen ? (
								<button
									type="button"
									onClick={() => setIsDescriptionOpen(true)}
									className="training-form-description-toggle"
								>
									<Plus aria-hidden="true" />
									<span>Добавить описание</span>
									<small>(необязательно)</small>
								</button>
							) : (
								<AnimatePresence initial={false}>
									<motion.label
										className="training-form-description-field"
										initial={{ height: 0, opacity: 0 }}
										animate={{ height: 'auto', opacity: 1 }}
										exit={{ height: 0, opacity: 0 }}
									>
										<textarea
											value={description}
											maxLength={DESCRIPTION_LIMIT}
											onChange={(event) =>
												updateDescription(event.target.value)
											}
											placeholder="Описание тренировки"
											rows={3}
											aria-label="Описание тренировки"
										/>
										<span>{description.length}/{DESCRIPTION_LIMIT}</span>
									</motion.label>
								</AnimatePresence>
							)}
						</div>

						<section className="training-form-exercises">
							<div className="training-form-exercises-head">
								<h3>{formatExerciseCount(exerciseParams.length)}</h3>
							</div>
							<ExercisesAccordion
								onDelete={deleteExercise}
								exerciseParams={exerciseParams}
								setExerciseParams={setExerciseParams}
								addedExercises={exerciseParams}
								onAddExercise={closeModal}
							/>
						</section>
					</div>

					<footer className="training-form-footer">
						<button
							type="button"
							className="training-form-primary"
							onClick={handleMainButtonClick}
							disabled={!canSubmit}
						>
							{isLoading ? 'Создаём...' : mainButtonTitle}
						</button>
						{!hideSecondaryAction && (
							<button
								type="button"
								className="training-form-secondary"
								onClick={handleClearClick}
							>
								{onDelete ? 'Удалить тренировку' : 'Очистить всё'}
							</button>
						)}
						<p>
							<Lock aria-hidden="true" />
							Тренировка будет сохранена только у вас
						</p>
					</footer>
				</motion.div>
			</Modal>
			{showErrorModal && (
				<ErrorModal
					title={'Не удалось создать тренировку'}
					onRetry={() => {
						setShowErrorModal(false)
						void handleMainButtonClick()
					}}
					onClose={() => setShowErrorModal(false)}
				/>
			)}
			</>
		)
	},
)
